// Request building and response reading for PressroomList. Plain JS so the jest
// pipeline (no TypeScript preset) can test it.

export const API_SOURCES = {
  PRESS_ROOM: 'Press Room',
  APP_FEED: 'FF App Feed',
};

export const DEFAULT_PAGE_SIZE = 5;

export const DEFAULT_API_PATH = '/api/press-room/info/official';

// The app feed lives on a separate host per region, each answering in its own language
export const DEFAULT_APP_FEED_URLS = {
  US: 'https://app-us.ff.com/api/v2/user/live2',
  CN: 'https://app-cn.faradayfuturecn.com/api/v2/user/live2',
};

export function getDefaultAppFeedUrl(region) {
  return DEFAULT_APP_FEED_URLS[region] || DEFAULT_APP_FEED_URLS.US;
}

// The app feed ignores search and year, so the widget loads it whole and filters
// in the browser. It ignores `size` too and pages with `limit`.
const APP_FEED_LIMIT = '1000';

export function isAppFeed(apiSource) {
  return apiSource === API_SOURCES.APP_FEED;
}

// Split the configured override into the endpoint it points at and the query
// params written on it. A value that already carries a host (https://..., //...)
// is used as is, a value holding only a query string ("tag=a", "?tag=a") keeps the
// default endpoint, anything else is treated as a path for the region API domain.
export function parseApiUrlOverride(apiUrlOverride, defaultEndpoint = DEFAULT_API_PATH) {
  const configured = (apiUrlOverride || '').trim();
  const isQueryOnly = configured.startsWith('?') ||
    (!/^(https?:)?\/\//i.test(configured) && !configured.includes('/') && configured.includes('='));

  const endpoint = isQueryOnly || !configured ? defaultEndpoint : configured;
  const query = isQueryOnly
    ? configured.replace(/^\?/, '')
    : (configured.includes('?') ? configured.slice(configured.indexOf('?') + 1) : '');
  const queryStart = endpoint.indexOf('?');

  return {
    hasHost: /^(https?:)?\/\//i.test(endpoint),
    path: queryStart === -1 ? endpoint : endpoint.slice(0, queryStart),
    overrides: new URLSearchParams(query)
  };
}

// A `size` on the override sets how many posts one page holds, so it seeds the
// list and becomes the Load More step rather than pinning the request parameter.
export function getPageSize(apiUrlOverride) {
  const size = Number(parseApiUrlOverride(apiUrlOverride).overrides.get('size'));

  return Number.isInteger(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
}

// The authors named by `author_id` on the override, one or more comma separated
// ("author_id=178", "author_id=178,12898"). The press room API filters by them on
// the server, the app feed ignores the param, so the widget filters its posts.
export function getAuthorIds(apiUrlOverride) {
  return parseApiUrlOverride(apiUrlOverride).overrides.getAll('author_id')
    .flatMap(value => value.split(','))
    .map(id => id.trim())
    .filter(Boolean);
}

// Build the request URL, letting the configured query params replace the ones the
// widget builds under the same key.
export function buildRequestUrl(apiUrlOverride, fetchDomain, searchParams, apiSource, region) {
  const appFeed = isAppFeed(apiSource);
  const { hasHost, path, overrides } = parseApiUrlOverride(
    apiUrlOverride,
    appFeed ? getDefaultAppFeedUrl(region) : DEFAULT_API_PATH
  );

  const base = hasHost
    ? path
    : `${fetchDomain}${path.startsWith('/') ? '' : '/'}${path}`;

  const params = appFeed
    ? new URLSearchParams({ limit: APP_FEED_LIMIT, page: '1' })
    : new URLSearchParams(searchParams.toString());
  const overridden = {};

  overrides.forEach((value, key) => {
    // size is consumed as the page size, the widget keeps control of the param
    if (key === 'size') {
      return;
    }

    // drop the built-in value once, then keep every value configured for that key
    if (!overridden[key]) {
      overridden[key] = true;
      params.delete(key);
    }
    params.append(key, value);
  });

  const query = params.toString();

  return query ? `${base}?${query}` : base;
}

// The app feed's posts carry no link, so point at the app's article page, the
// same address the press room API gives for these posts.
function appFeedLink(id, requestUrl, region) {
  let origin = 'https://app-us.ff.com';

  try {
    origin = new URL(requestUrl).origin;
  } catch (error) {
    // keep the US app host
  }

  return `${origin}/ff-v3/news/${id}?lang=${region === 'CN' ? 'zh-CN' : 'en-US'}`;
}

function toPressroomPost(item, requestUrl, region) {
  const imageUrl = item.medias?.[0]?.image?.url || item.cover?.url;

  return {
    id: item.id,
    author_id: item.author_id,
    link: item.link || appFeedLink(item.id, requestUrl, region),
    title: item.title,
    summary: item.summary,
    content: item.content,
    medias: imageUrl ? [{ image: { url: imageUrl } }] : [],
    views_count: item.views_count,
    comments_count: item.comments_count,
    likes_count: item.likes_count,
    created_at: item.published_at || item.created_at
  };
}

// Read the posts out of either API's reply: the press room API answers
// { posts: [...] }, the app feed { data: [...] }. Anything else is an error.
export function readPosts(res, apiSource, requestUrl, region) {
  if (isAppFeed(apiSource)) {
    if (!Array.isArray(res?.data)) {
      throw new Error('the app feed answered without a data list');
    }
    return res.data.map(item => toPressroomPost(item, requestUrl, region));
  }

  if (!Array.isArray(res?.posts)) {
    throw new Error('the press room API answered without a posts list');
  }
  return res.posts;
}

// What the press room API does on the server for search, year and author, done
// on the app feed's posts in the browser.
export function filterPosts(posts, { search, year, authorIds = [] }) {
  const words = (search || '').trim().toLowerCase();
  const authors = new Set(authorIds.map(String));

  return posts.filter((post) => {
    if (authors.size && !authors.has(String(post.author_id))) {
      return false;
    }
    if (year && String(new Date(post.created_at).getUTCFullYear()) !== String(year)) {
      return false;
    }
    if (words) {
      const text = [post.title, post.summary, post.content].filter(Boolean).join(' ').toLowerCase();
      return text.includes(words);
    }
    return true;
  });
}
